import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/DataTable';
import BulkActions from '../../components/BulkActions';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import { toast } from 'sonner';
import {
  UserPlus,
  Settings,
  Users,
  DollarSign,
  Briefcase,
  Eye,
  Mail,
  Phone,
  MapPin,
  Pencil,
  Check,
  Home,
  AlertCircle,
  Coffee,
  UserX,
  History,
  Calendar,
  RotateCcw,
} from 'lucide-react';

const DUTY_TYPES = [
  { value: 'front_desk', label: 'Front Desk' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'academy_management', label: 'Academy Management' }
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: Check },
  inactive: { label: 'Inactive', color: 'bg-slate-100 text-slate-600', icon: UserX },
  on_break: { label: 'On Break', color: 'bg-amber-100 text-amber-700', icon: Coffee }
};

const StaffManagement = () => {
  // iter317 — Phase 7B plan gate. Pivots the Add Staff dialog to the
  // upgrade modal when the tenant's plan lacks `core.staff`.
  const staffFeature = useFeature('core.staff');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [dutiesDialogOpen, setDutiesDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [visibleColumns, setVisibleColumns] = useState([]);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Duty rate warning
  const [dutyRateWarningDialog, setDutyRateWarningDialog] = useState(false);
  const [dutiesWithoutRates, setDutiesWithoutRates] = useState([]);

  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  
  const [dutyAssignment, setDutyAssignment] = useState({
    assigned_duties: [],
    duty_rates: {}
  });

  // Status change form
  const [statusChange, setStatusChange] = useState({
    status: 'active',
    effective_date: new Date().toISOString().split('T')[0],
    reason: '',
    expected_return_date: ''
  });

  // Status history
  const [statusHistory, setStatusHistory] = useState([]);

  // Edit form state
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      // Fetch all staff (including inactive) and filter on frontend
      const response = await api.get('/staff?status=all');
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post('/staff', {
        ...newStaff,
        role: 'staff'
      });
      toast.success('Staff member created');
      setCreateDialogOpen(false);
      setNewStaff({ name: '', email: '', password: '', phone: '' });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    setDeleting(true);
    try {
      await api.delete(`/staff/${staffId}`);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate staff');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
    }
  };

  const openDeleteDialog = (staffMember) => {
    setStaffToDelete(staffMember);
    setDeleteDialogOpen(true);
  };

  const handleReactivateStaff = async (id) => {
    try {
      await api.put(`/staff/${id}/reactivate`);
      toast.success('Staff member reactivated');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate staff');
    }
  };

  // Status management functions
  const openStatusDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    const currentStatus = staffMember.profile?.status || 'active';
    setStatusChange({
      status: currentStatus,
      effective_date: new Date().toISOString().split('T')[0],
      reason: '',
      expected_return_date: ''
    });
    setStatusDialog(true);
  };

  const handleStatusChange = async () => {
    if (!selectedStaff) return;
    setSaving(true);
    try {
      await api.put(`/admin/staff/${selectedStaff.id}/status`, statusChange);
      toast.success(`Staff status updated to ${STATUS_CONFIG[statusChange.status]?.label || statusChange.status}`);
      setStatusDialog(false);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const openHistoryDialog = async (staffMember) => {
    setSelectedStaff(staffMember);
    try {
      const response = await api.get(`/admin/staff/${staffMember.id}/status-history`);
      setStatusHistory(response.data || []);
      setHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load status history');
    }
  };

  const openDutiesDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    const profile = staffMember.profile || {};
    const rates = {};
    // Handle both old format (duty_type/hourly_rate) and new format (duty/rate)
    (profile.duty_rates || []).forEach(dr => {
      const dutyKey = dr.duty || dr.duty_type;
      const rateValue = dr.rate !== undefined ? dr.rate : dr.hourly_rate;
      if (dutyKey) {
        rates[dutyKey] = rateValue;
      }
    });
    setDutyAssignment({
      assigned_duties: profile.assigned_duties || [],
      duty_rates: rates
    });
    setDutiesDialogOpen(true);
  };

  const viewStaffDetails = (staffMember) => {
    setSelectedStaff(staffMember);
    setDetailsDialog(true);
  };

  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    const profile = staffMember.profile || {};
    setEditData({
      name: staffMember.name || '',
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      zip: profile.zip || '',
      emergency_contact_name: profile.emergency_contact_name || '',
      emergency_contact_phone: profile.emergency_contact_phone || '',
      notes: profile.admin_notes || ''
    });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/staff/${selectedStaff.id}/profile`, editData);
      toast.success(`Staff profile updated for ${editData.name}`);
      setEditDialog(false);
      fetchStaff();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update staff profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleDuty = (duty) => {
    setDutyAssignment(prev => {
      const duties = prev.assigned_duties.includes(duty)
        ? prev.assigned_duties.filter(d => d !== duty)
        : [...prev.assigned_duties, duty];
      return { ...prev, assigned_duties: duties };
    });
  };

  const updateDutyRate = (duty, rate) => {
    setDutyAssignment(prev => ({
      ...prev,
      duty_rates: { ...prev.duty_rates, [duty]: parseFloat(rate) || 0 }
    }));
  };

  const handleSaveDuties = async () => {
    if (!selectedStaff) return;
    
    // Format duty_rates to match backend schema (duty, rate)
    const dutyRates = dutyAssignment.assigned_duties.map(duty => ({
      duty: duty,
      rate: dutyAssignment.duty_rates[duty] || 0
    }));
    
    // Check for missing rates
    const missingRates = dutyAssignment.assigned_duties.filter(
      duty => !dutyAssignment.duty_rates[duty] || dutyAssignment.duty_rates[duty] === 0
    );
    
    // If there are duties without rates, show confirmation dialog
    if (missingRates.length > 0) {
      setDutiesWithoutRates(missingRates);
      setDutyRateWarningDialog(true);
      return;
    }
    
    // Otherwise proceed directly
    await saveDutiesToBackend(dutyRates);
  };

  const saveDutiesToBackend = async (dutyRates = null) => {
    if (!dutyRates) {
      dutyRates = dutyAssignment.assigned_duties.map(duty => ({
        duty: duty,
        rate: dutyAssignment.duty_rates[duty] || 0
      }));
    }
    
    try {
      const response = await api.put(`/admin/staff/${selectedStaff.id}/duties`, {
        assigned_duties: dutyAssignment.assigned_duties,
        duty_rates: dutyRates
      });
      
      // Show warning from backend if any
      if (response.data.warning) {
        toast.warning(response.data.warning);
      } else {
        toast.success('Staff duties updated');
      }
      
      setDutiesDialogOpen(false);
      setDutyRateWarningDialog(false);
      setDutiesWithoutRates([]);
      fetchStaff();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to update duties');
      }
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => {
        const status = row.profile?.status || 'active';
        const StatusIcon = STATUS_CONFIG[status]?.icon || Check;
        return (
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              status === 'active' ? 'bg-blue-100' : 
              status === 'on_break' ? 'bg-amber-100' : 'bg-slate-100'
            }`}>
              <span className={`font-semibold ${
                status === 'active' ? 'text-blue-600' : 
                status === 'on_break' ? 'text-amber-600' : 'text-slate-500'
              }`}>
                {row.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-900">{row.name}</p>
                {row.numeric_id && (
                  <span className="text-xs text-slate-400 font-mono">ID: {row.numeric_id}</span>
                )}
              </div>
              <Badge className={`text-xs ${STATUS_CONFIG[status]?.color}`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {STATUS_CONFIG[status]?.label}
              </Badge>
            </div>
          </div>
        );
      }
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Mail className="h-3 w-3" />
          {row.email}
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Phone className="h-3 w-3" />
          {row.phone}
        </div>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'profile.assigned_duties',
      label: 'Assigned Duties',
      render: (row) => row.profile?.assigned_duties?.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {row.profile.assigned_duties.map(duty => (
            <Badge key={duty} className="bg-blue-100 text-blue-700 text-xs">
              {DUTY_TYPES.find(d => d.value === duty)?.label || duty}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-slate-400 text-sm">None assigned</span>
      )
    }
  ];

  const actions = (row) => (
    <div className="flex gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => viewStaffDetails(row)}
        data-testid={`view-staff-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => openEditDialog(row)}
        data-testid={`edit-staff-${row.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => openDutiesDialog(row)}
        data-testid={`edit-duties-${row.id}`}
      >
        <Settings className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => openStatusDialog(row)}
        data-testid={`status-staff-${row.id}`}
        title="Change Status"
      >
        {STATUS_CONFIG[row.profile?.status || 'active']?.icon && 
          <Coffee className="h-4 w-4" />
        }
      </Button>
      {row.profile?.status === 'inactive' ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleReactivateStaff(row.id)}
          className="text-green-600 hover:text-green-700 hover:bg-green-50"
          data-testid={`reactivate-staff-${row.id}`}
          title="Reactivate Staff"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openDeleteDialog(row)}
          className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
          data-testid={`deactivate-staff-${row.id}`}
          title="Deactivate Staff"
        >
          <UserX className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  // Filter staff by status tab
  const getFilteredStaff = () => {
    if (activeTab === 'all') return staff;
    return staff.filter(s => (s.profile?.status || 'active') === activeTab);
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Management">
      <div className="space-y-6" data-testid="staff-management-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage staff accounts, status and duty assignments</p>
          <div className="flex flex-wrap items-center gap-3">
            <ColumnConfig
              columns={columns}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="admin-staff-columns"
              requiredColumns={['name']}
            />
            <BulkActions
              entityName="Staff"
              exportEndpoint="/admin/staff/export"
              uploadEndpoint="/admin/staff/bulk-upload"
              onUploadComplete={fetchStaff}
              sampleFields={['name', 'email', 'phone', 'status', 'address', 'emergency_contact_name', 'emergency_contact_phone']}
            />
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              // iter317 — Phase 7B plan gate. Pivot Add Staff click to
              // the upgrade modal when the tenant's plan lacks
              // `core.staff` instead of opening an empty form.
              if (open && !staffFeature.enabled) {
                staffFeature.prompt('Adding a staff member');
                return;
              }
              setCreateDialogOpen(open);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600" data-testid="add-staff-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Staff
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Staff Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateStaff} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                    required
                    data-testid="staff-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                    required
                    data-testid="staff-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                    required
                    data-testid="staff-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={newStaff.phone}
                    onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                    data-testid="staff-phone-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="create-staff-btn">
                    Create Staff
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Staff</p>
                <p className="font-heading text-2xl font-bold text-slate-900">{staff.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="font-heading text-2xl font-bold text-green-600">
                  {staff.filter(s => (s.profile?.status || 'active') === 'active').length}
                </p>
              </div>
              <Check className="h-8 w-8 text-green-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">On Break</p>
                <p className="font-heading text-2xl font-bold text-amber-600">
                  {staff.filter(s => s.profile?.status === 'on_break').length}
                </p>
              </div>
              <Coffee className="h-8 w-8 text-amber-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Inactive</p>
                <p className="font-heading text-2xl font-bold text-slate-500">
                  {staff.filter(s => s.profile?.status === 'inactive').length}
                </p>
              </div>
              <UserX className="h-8 w-8 text-slate-400" />
            </CardContent>
          </Card>
        </div>

        {/* Status Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 p-1 sm:inline-flex sm:w-auto sm:grid-cols-none">
            <TabsTrigger className="min-w-0 whitespace-normal px-2 py-2 text-xs sm:whitespace-nowrap sm:text-sm" value="all" data-testid="tab-all">
              All ({staff.length})
            </TabsTrigger>
            <TabsTrigger className="min-w-0 whitespace-normal px-2 py-2 text-xs sm:whitespace-nowrap sm:text-sm" value="active" data-testid="tab-active">
              <Check className="h-4 w-4 shrink-0 mr-1" />
              Active ({staff.filter(s => (s.profile?.status || 'active') === 'active').length})
            </TabsTrigger>
            <TabsTrigger className="min-w-0 whitespace-normal px-2 py-2 text-xs sm:whitespace-nowrap sm:text-sm" value="on_break" data-testid="tab-on-break">
              <Coffee className="h-4 w-4 shrink-0 mr-1" />
              On Break ({staff.filter(s => s.profile?.status === 'on_break').length})
            </TabsTrigger>
            <TabsTrigger className="min-w-0 whitespace-normal px-2 py-2 text-xs sm:whitespace-nowrap sm:text-sm" value="inactive" data-testid="tab-inactive">
              <UserX className="h-4 w-4 shrink-0 mr-1" />
              Inactive ({staff.filter(s => s.profile?.status === 'inactive').length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Staff Table */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <DataTable
              data={getFilteredStaff()}
              columns={columns}
              visibleColumns={visibleColumns}
              actions={actions}
              emptyIcon={Users}
              emptyMessage={activeTab === 'all' ? "No staff members yet" : `No ${activeTab.replace('_', ' ')} staff`}
              searchable={true}
              searchFields={['name', 'email', 'phone']}
              testIdPrefix="staff"
            />
          </CardContent>
        </Card>

        {/* Staff Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-500" />
                Staff Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedStaff && (
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">
                        {selectedStaff.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-slate-900">
                        {selectedStaff.name}
                      </h3>
                      <p className="text-slate-600 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {selectedStaff.email}
                      </p>
                      {selectedStaff.phone && (
                        <p className="text-slate-600 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {selectedStaff.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Duties & Rates */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Assigned Duties & Rates
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedStaff.profile?.assigned_duties?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedStaff.profile.assigned_duties.map(duty => {
                          const rateObj = selectedStaff.profile.duty_rates?.find(dr => (dr.duty || dr.duty_type) === duty);
                          const rateValue = rateObj ? (rateObj.rate !== undefined ? rateObj.rate : rateObj.hourly_rate) : 0;
                          return (
                            <div key={duty} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <span className="font-medium">
                                {DUTY_TYPES.find(d => d.value === duty)?.label || duty}
                              </span>
                              <Badge className="bg-green-100 text-green-700">
                                {cs()}{rateValue || 0}/hr
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No duties assigned</p>
                    )}
                  </CardContent>
                </Card>

                {/* Address */}
                {selectedStaff.profile?.address && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Address
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {typeof selectedStaff.profile.address === 'object' ? (
                        <div className="text-sm">
                          {selectedStaff.profile.address.street && <p>{selectedStaff.profile.address.street}</p>}
                          <p>
                            {selectedStaff.profile.address.city && selectedStaff.profile.address.city}
                            {selectedStaff.profile.address.state && `, ${selectedStaff.profile.address.state}`}
                            {selectedStaff.profile.address.zip_code && ` ${selectedStaff.profile.address.zip_code}`}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm">{selectedStaff.profile.address}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Emergency Contact */}
                {selectedStaff.profile?.emergency_contact && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Emergency Contact</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {typeof selectedStaff.profile.emergency_contact === 'object' ? (
                        <div className="text-sm">
                          {selectedStaff.profile.emergency_contact.name && (
                            <p className="font-medium">{selectedStaff.profile.emergency_contact.name}</p>
                          )}
                          {selectedStaff.profile.emergency_contact.phone && (
                            <p>{selectedStaff.profile.emergency_contact.phone}</p>
                          )}
                          {selectedStaff.profile.emergency_contact.relationship && (
                            <p className="text-slate-500">{selectedStaff.profile.emergency_contact.relationship}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm">{selectedStaff.profile.emergency_contact}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Member Since */}
                <div className="text-sm text-slate-500 pt-4 border-t">
                  Member since: {new Date(selectedStaff.created_at).toLocaleDateString()}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Duties Assignment Dialog */}
        <Dialog open={dutiesDialogOpen} onOpenChange={setDutiesDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Assign Duties & Rates
              </DialogTitle>
            </DialogHeader>
            {selectedStaff && (
              <div className="space-y-6 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedStaff.name}</p>
                  <p className="text-sm text-slate-500">{selectedStaff.email}</p>
                </div>

                <div className="space-y-4">
                  {DUTY_TYPES.map(duty => (
                    <div key={duty.value} className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={duty.value}
                          checked={dutyAssignment.assigned_duties.includes(duty.value)}
                          onCheckedChange={() => toggleDuty(duty.value)}
                          data-testid={`duty-checkbox-${duty.value}`}
                        />
                        <label htmlFor={duty.value} className="font-medium cursor-pointer">
                          {duty.label}
                        </label>
                      </div>
                      {dutyAssignment.assigned_duties.includes(duty.value) && (
                        <div className="ml-6 flex items-center gap-2">
                          <span className="text-sm text-slate-500">Rate:</span>
                          <div className="relative">
                            <DollarSign className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={dutyAssignment.duty_rates[duty.value] || ''}
                              onChange={(e) => updateDutyRate(duty.value, e.target.value)}
                              className="pl-7 w-32"
                              placeholder="0.00"
                              data-testid={`rate-input-${duty.value}`}
                            />
                          </div>
                          <span className="text-sm text-slate-500">/hr</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setDutiesDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={handleSaveDuties}
                    data-testid="save-duties-btn"
                  >
                    Save Duties
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Staff Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-500" />
                Edit Staff Profile
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-name">Full Name *</Label>
                    <Input
                      id="edit-staff-name"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      placeholder="Enter full name"
                      data-testid="edit-staff-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-email">Email *</Label>
                    <Input
                      id="edit-staff-email"
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      placeholder="Enter email"
                      data-testid="edit-staff-email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-staff-phone">Phone</Label>
                  <Input
                    id="edit-staff-phone"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    data-testid="edit-staff-phone"
                  />
                </div>
              </div>

              {/* Address Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Home className="h-4 w-4 text-green-500" />
                  Address
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-staff-address">Street Address</Label>
                  <Input
                    id="edit-staff-address"
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    placeholder="Enter street address"
                    data-testid="edit-staff-address"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-city">City</Label>
                    <Input
                      id="edit-staff-city"
                      value={editData.city}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      placeholder="City"
                      data-testid="edit-staff-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-state">State</Label>
                    <Input
                      id="edit-staff-state"
                      value={editData.state}
                      onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                      placeholder="State"
                      data-testid="edit-staff-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-zip">ZIP Code</Label>
                    <Input
                      id="edit-staff-zip"
                      value={editData.zip}
                      onChange={(e) => setEditData({ ...editData, zip: e.target.value })}
                      placeholder="ZIP"
                      data-testid="edit-staff-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-red-500" />
                  Emergency Contact
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-emergency-name">Contact Name</Label>
                    <Input
                      id="edit-staff-emergency-name"
                      value={editData.emergency_contact_name}
                      onChange={(e) => setEditData({ ...editData, emergency_contact_name: e.target.value })}
                      placeholder="Emergency contact name"
                      data-testid="edit-staff-emergency-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-staff-emergency-phone">Contact Phone</Label>
                    <Input
                      id="edit-staff-emergency-phone"
                      value={editData.emergency_contact_phone}
                      onChange={(e) => setEditData({ ...editData, emergency_contact_phone: e.target.value })}
                      placeholder="Emergency contact phone"
                      data-testid="edit-staff-emergency-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="edit-staff-notes">Admin Notes</Label>
                <Textarea
                  id="edit-staff-notes"
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  placeholder="Internal notes about this staff member..."
                  rows={3}
                  data-testid="edit-staff-notes"
                />
              </div>

              <DialogFooter className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  data-testid="save-staff-btn"
                >
                  {saving ? (
                    <span className="animate-spin mr-1">⏳</span>
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Coffee className="h-5 w-5 text-amber-500" />
                Change Staff Status
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Current Status */}
              {selectedStaff && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedStaff.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">Current Status:</span>
                    <Badge className={STATUS_CONFIG[selectedStaff.profile?.status || 'active']?.color}>
                      {STATUS_CONFIG[selectedStaff.profile?.status || 'active']?.label}
                    </Badge>
                  </div>
                </div>
              )}

              {/* New Status */}
              <div className="space-y-2">
                <Label>New Status *</Label>
                <Select
                  value={statusChange.status}
                  onValueChange={(value) => setStatusChange({ ...statusChange, status: value })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="on_break">
                      <div className="flex items-center gap-2">
                        <Coffee className="h-4 w-4 text-amber-500" />
                        On Break / Holiday
                      </div>
                    </SelectItem>
                    <SelectItem value="inactive">
                      <div className="flex items-center gap-2">
                        <UserX className="h-4 w-4 text-slate-500" />
                        Inactive
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Effective Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Effective Date *
                </Label>
                <Input
                  type="date"
                  value={statusChange.effective_date}
                  onChange={(e) => setStatusChange({ ...statusChange, effective_date: e.target.value })}
                  data-testid="status-effective-date"
                />
              </div>

              {/* Expected Return Date (only for on_break) */}
              {statusChange.status === 'on_break' && (
                <div className="space-y-2">
                  <Label>Expected Return Date</Label>
                  <Input
                    type="date"
                    value={statusChange.expected_return_date}
                    onChange={(e) => setStatusChange({ ...statusChange, expected_return_date: e.target.value })}
                    data-testid="status-return-date"
                  />
                </div>
              )}

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  value={statusChange.reason}
                  onChange={(e) => setStatusChange({ ...statusChange, reason: e.target.value })}
                  placeholder="Reason for status change..."
                  data-testid="status-reason"
                  rows={2}
                />
              </div>

              {/* Warning for non-active status */}
              {statusChange.status !== 'active' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {statusChange.status === 'on_break' 
                      ? 'Staff on break cannot be assigned to new shifts. Existing future shifts will be cancelled.'
                      : 'Inactive staff cannot be assigned to shifts. Existing future shifts will be cancelled.'
                    }
                  </p>
                </div>
              )}

              <DialogFooter className="flex justify-between pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => openHistoryDialog(selectedStaff)}
                  data-testid="view-history-btn"
                >
                  <History className="h-4 w-4 mr-1" />
                  View History
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStatusDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleStatusChange}
                    disabled={saving}
                    className={statusChange.status === 'active' ? 'bg-green-500 hover:bg-green-600' : 
                               statusChange.status === 'on_break' ? 'bg-amber-500 hover:bg-amber-600' :
                               'bg-slate-500 hover:bg-slate-600'}
                    data-testid="confirm-status-btn"
                  >
                    {saving ? 'Saving...' : 'Update Status'}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Status History Dialog */}
        <Dialog open={historyDialog} onOpenChange={setHistoryDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <History className="h-5 w-5 text-blue-500" />
                Status History - {selectedStaff?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              {statusHistory.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No status history available</p>
              ) : (
                <div className="space-y-3">
                  {statusHistory.slice().reverse().map((entry, index) => (
                    <div key={entry.id || index} className="p-3 bg-slate-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center gap-2 mb-1">
                        {entry.from_status && (
                          <>
                            <Badge className={STATUS_CONFIG[entry.from_status]?.color || 'bg-slate-100'}>
                              {STATUS_CONFIG[entry.from_status]?.label || entry.from_status}
                            </Badge>
                            <span className="text-slate-400">→</span>
                          </>
                        )}
                        <Badge className={STATUS_CONFIG[entry.to_status]?.color || 'bg-slate-100'}>
                          {STATUS_CONFIG[entry.to_status]?.label || entry.to_status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        <strong>Effective:</strong> {new Date(entry.effective_date).toLocaleDateString()}
                      </p>
                      {entry.expected_return_date && (
                        <p className="text-sm text-blue-600">
                          <strong>Expected Return:</strong> {new Date(entry.expected_return_date).toLocaleDateString()}
                        </p>
                      )}
                      {entry.reason && (
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>Reason:</strong> {entry.reason}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Changed by {entry.changed_by_name} on {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => setHistoryDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duty Rate Warning Dialog */}
        <AlertDialog open={dutyRateWarningDialog} onOpenChange={setDutyRateWarningDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Missing Duty Rates
              </AlertDialogTitle>
              <AlertDialogDescription>
                The following duties have no rate set: <strong>{dutiesWithoutRates.map(d => DUTY_TYPES.find(dt => dt.value === d)?.label || d).join(', ')}</strong>.
                <br /><br />
                Do you want to save anyway?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => saveDutiesToBackend()}
                className="bg-amber-500 hover:bg-amber-600"
              >
                Save Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Deactivate Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate Staff Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to deactivate <strong>{staffToDelete?.name}</strong>? 
                They will no longer appear in the active list, but their history will be preserved.
                You can reactivate them at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteStaff(staffToDelete?.id)}
                disabled={deleting}
                className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              >
                {deleting ? 'Deactivating...' : 'Deactivate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffManagement;
